import type { LogicConfig, LogicResult } from '../types';
import type { QuestionValue } from '@lib/types/survey';
import { SKIPPED_VALUE, ANY_KEY_VALUE } from '@lib/utils/questionRules';

/**
 * Process skip logic configuration
 * @param config The skip logic configuration
 * @param value The current question value (selected option key)
 */
export async function processSkipLogic(
  config: LogicConfig,
  value: QuestionValue
): Promise<LogicResult> {
  // Find the skip logic configuration in the array
  const skipLogicConfig = Array.isArray(config) 
    ? config.find(logic => logic.name === 'skipLogic')
    : config;

  // If no skip logic is configured, return standard progression
  if (!skipLogicConfig?.skips) {
    return {
      success: true,
      nextAction: {
        type: 'standard',
        value: 0
      }
    };
  }

  // First, specifically check for __ANY_KEY__ for unconditional skips
  const anyKeySkip = skipLogicConfig.skips.find((skipConfig: { key: string | number, skip: number | number[] }) => 
    skipConfig.key === ANY_KEY_VALUE
  );

  // If __ANY_KEY__ is found, apply it immediately
  if (anyKeySkip) {
    let skipAmount = 0;
    if (anyKeySkip.skip !== undefined) {
      if (Array.isArray(anyKeySkip.skip)) {
        const randomIndex = Math.floor(Math.random() * anyKeySkip.skip.length);
        skipAmount = anyKeySkip.skip[randomIndex];
      } else {
        skipAmount = anyKeySkip.skip;
      }
    }
    return {
      success: true,
      nextAction: {
        type: 'skip',
        value: skipAmount
      }
    };
  }

  // If __ANY_KEY__ is not found, THEN check if a value was provided.
  // If no value is selected (and no __ANY_KEY__), return standard progression.
  if (!value) {
    return {
      success: true,
      nextAction: {
        type: 'standard',
        value: 0
      }
    };
  }

  // Enhanced skip logic with AND/OR operators
  let matchingSkip = null;
  const selectedValues = Array.isArray(value) ? value.map(v => v?.toString()) : [value?.toString()];
  
  // Check each skip configuration for a match
  for (const skipConfig of skipLogicConfig.skips) {
    const config = skipConfig as { 
      key?: string | number, 
      keys?: (string | number)[], 
      operator?: 'AND' | 'OR',
      skip: number | number[] 
    };
    
    // New format: keys with operator (AND/OR)
    if (config.keys && Array.isArray(config.keys)) {
      const requiredKeys = config.keys.map(k => k.toString());
      const operator = config.operator || 'OR'; // Default to OR
      
      let conditionMet = false;
      if (operator === 'AND') {
        // ALL required keys must be selected
        conditionMet = requiredKeys.every(reqKey => 
          selectedValues.some(selKey => selKey === reqKey)
        );
      } else { // OR
        // ANY of the required keys must be selected
        conditionMet = requiredKeys.some(reqKey => 
          selectedValues.some(selKey => selKey === reqKey)
        );
      }
      
      if (conditionMet) {
        matchingSkip = config;
        break;
      }
    }
    // Single key format
    else if (config.key) {
      const targetKey = config.key.toString();
      const hasMatch = selectedValues.some(selKey => selKey === targetKey);
      
      if (hasMatch) {
        matchingSkip = config;
        break;
      }
    }
  }

  if (matchingSkip) {
    // Handle skip property that can be either number or array
    let skipAmount = 0;
    
    if (matchingSkip.skip !== undefined) {
      if (Array.isArray(matchingSkip.skip)) {
        // If skip is an array, randomly select one
        const randomIndex = Math.floor(Math.random() * matchingSkip.skip.length);
        skipAmount = matchingSkip.skip[randomIndex];
      } else {
        // If skip is a single number, use that
        skipAmount = matchingSkip.skip;
      }
    }

    return {
      success: true,
      nextAction: {
        type: 'skip',
        value: skipAmount
      }
    };
  }

  // No matching skip configuration found, return standard progression
  return {
    success: true,
    nextAction: {
      type: 'standard',
      value: 0
    }
  };
} 